import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ShieldAlert, ShieldCheck, KeyRound, Mail, User as UserIcon, Send, Eye, EyeOff } from "lucide-react";
import { AuthContext } from "../../context/AuthContext";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const cardCls    = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls   = "w-full p-2.5 rounded-lg bg-black/40 border border-white/20 text-white placeholder-white/30 outline-none focus:border-[#00FF99]/60 text-sm disabled:opacity-40 disabled:cursor-not-allowed";
const primaryBtn = "inline-flex items-center gap-2 bg-[#00FF99] text-black font-semibold px-4 py-2 rounded-lg hover:bg-[#00cc7a] transition disabled:opacity-40 disabled:cursor-not-allowed";

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
});

// Mask the local part of an email for display
function maskEmail(email) {
  if (!email || !email.includes("@")) return email || "your email";
  const [local, domain] = email.split("@");
  if (local.length <= 1) return `${local}***@${domain}`;
  return `${local[0]}***@${domain}`;
}

function fmtMmSs(secs) {
  const s = Math.max(0, Math.floor(secs));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

// ──────────────────────────────────────────────────────────────
// MFA Banner — collapses three visual states into one component
// ──────────────────────────────────────────────────────────────
function MfaBanner({
  state,            // "unverified" | "code-sent" | "verifying" | "verified" | "expired"
  email,
  onRequestOtp,
  otp,
  setOtp,
  onVerify,
  otpRequesting,
  otpVerifying,
  otpExpiresIn,     // 5-min countdown after request
  mfaTimeLeft,      // 10-min change-window countdown
  errorMsg,
}) {
  if (state === "verified") {
    return (
      <div style={{
        background:   "rgba(0,255,153,0.06)",
        border:       "1px solid rgba(0,255,153,0.2)",
        borderRadius: 10,
        padding:      "14px 16px",
      }} className="flex items-center justify-between gap-4 mb-5">
        <div className="flex items-center gap-2 text-[#00FF99]">
          <ShieldCheck size={16} />
          <span className="text-sm font-medium">Identity verified — changes enabled</span>
        </div>
        <span className="text-xs text-[#00FF99]/70 font-mono">
          Session expires in {fmtMmSs(mfaTimeLeft)}
        </span>
      </div>
    );
  }

  // amber states
  return (
    <div style={{
      background:   "rgba(255,180,0,0.08)",
      border:       "1px solid rgba(255,180,0,0.3)",
      borderRadius: 10,
      padding:      "14px 16px",
      color:        "rgba(255,255,255,0.8)",
    }} className="mb-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <ShieldAlert size={16} className="text-amber-400" />
          <span className="text-sm font-medium">
            {state === "expired"
              ? "MFA session expired — please verify again"
              : state === "code-sent"
                ? `Enter the code sent to ${maskEmail(email)}`
                : "Changes require MFA verification"}
          </span>
        </div>
        {state !== "code-sent" && (
          <button
            onClick={onRequestOtp}
            disabled={otpRequesting}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-amber-400/10 text-amber-300 border border-amber-400/30 hover:bg-amber-400/15 disabled:opacity-50"
          >
            <Send size={12} />
            {otpRequesting ? "Sending…" : "Send OTP to my email"}
          </button>
        )}
      </div>

      {state === "code-sent" && (
        <div className="mt-4 flex flex-col items-center gap-3">
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="000000"
            style={{
              width:         160,
              fontSize:      24,
              letterSpacing: 8,
              textAlign:     "center",
              padding:       "10px 12px",
              borderRadius:  10,
              background:    "rgba(0,0,0,0.4)",
              border:        "1px solid rgba(255,255,255,0.15)",
              color:         "#fff",
              outline:       "none",
              fontFamily:    "monospace",
            }}
            autoFocus
          />
          <div className="flex items-center gap-3">
            <button
              onClick={onVerify}
              disabled={otpVerifying || otp.length !== 6}
              className={primaryBtn}
              style={{ minWidth: 120 }}
            >
              {otpVerifying ? "Verifying…" : "Verify"}
            </button>
            <span className="text-xs text-amber-300/80 font-mono">
              Code expires in {fmtMmSs(otpExpiresIn)}
            </span>
          </div>
          <button
            onClick={onRequestOtp}
            disabled={otpRequesting}
            className="text-[11px] text-amber-300/70 hover:text-amber-300 underline"
          >
            Resend code
          </button>
        </div>
      )}

      {errorMsg && (
        <p className="text-red-400 text-xs mt-3 text-center" role="alert">{errorMsg}</p>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Main page
// ──────────────────────────────────────────────────────────────
export default function SuperadminSettings() {
  const auth = useContext(AuthContext);
  const user = auth?.user || {};

  // ── MFA state machine ──────────────────────────────────
  // unverified → code-sent → (verify) → verified → (timeout) → expired
  const [mfaState,       setMfaState]      = useState("unverified");
  const [otp,            setOtp]           = useState("");
  const [otpRequesting,  setOtpRequesting] = useState(false);
  const [otpVerifying,   setOtpVerifying]  = useState(false);
  const [otpExpiresAt,   setOtpExpiresAt]  = useState(0);   // ms epoch
  const [mfaExpiresAt,   setMfaExpiresAt]  = useState(0);   // ms epoch
  const [mfaError,       setMfaError]      = useState("");

  // ── Form state ─────────────────────────────────────────
  const [emailDraft,    setEmailDraft]    = useState("");
  const [emailMsg,      setEmailMsg]      = useState(null);
  const [emailSaving,   setEmailSaving]   = useState(false);

  const [pwCurrent,     setPwCurrent]     = useState("");
  const [pwNew,         setPwNew]         = useState("");
  const [pwConfirm,     setPwConfirm]     = useState("");
  const [pwShowCurrent, setPwShowCurrent] = useState(false);
  const [pwShowNew,     setPwShowNew]     = useState(false);
  const [pwMsg,         setPwMsg]         = useState(null);
  const [pwSaving,      setPwSaving]      = useState(false);

  const [nameDraft,     setNameDraft]     = useState("");
  const [nameMsg,       setNameMsg]       = useState(null);
  const [nameSaving,    setNameSaving]    = useState(false);

  // Initialise drafts from auth user
  useEffect(() => {
    setEmailDraft(user.email || "");
    setNameDraft(user.full_name || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.email, user.full_name]);

  // ── Tick clock once a second for both countdowns ───────
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const otpTimeLeft = Math.max(0, Math.floor((otpExpiresAt - now) / 1000));
  const mfaTimeLeft = Math.max(0, Math.floor((mfaExpiresAt - now) / 1000));

  // Auto-transition: code expires before user verifies
  useEffect(() => {
    if (mfaState === "code-sent" && otpExpiresAt && otpTimeLeft <= 0) {
      setMfaState("expired");
      setMfaError("Code expired before verification.");
    }
  }, [mfaState, otpExpiresAt, otpTimeLeft]);

  // Auto-transition: verified session times out
  useEffect(() => {
    if (mfaState === "verified" && mfaExpiresAt && mfaTimeLeft <= 0) {
      setMfaState("expired");
      setMfaError("MFA session expired. Please verify again.");
    }
  }, [mfaState, mfaExpiresAt, mfaTimeLeft]);

  // ── MFA handlers ───────────────────────────────────────
  const requestOtp = useCallback(async () => {
    setMfaError("");
    setOtp("");
    setOtpRequesting(true);
    try {
      const res = await fetch(`${API_BASE}/api/superadmin/account/mfa/request`, {
        method: "POST", headers: authHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setOtpExpiresAt(Date.now() + (data.expires_in || 300) * 1000);
      setMfaState("code-sent");
    } catch (e) {
      setMfaError(e.message);
    } finally {
      setOtpRequesting(false);
    }
  }, []);

  const verifyOtp = useCallback(async () => {
    setMfaError("");
    setOtpVerifying(true);
    try {
      const res = await fetch(`${API_BASE}/api/superadmin/account/mfa/verify`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ otp_code: otp }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setMfaState("verified");
      setMfaExpiresAt(Date.now() + (data.valid_for_seconds || 600) * 1000);
      setOtp("");
    } catch (e) {
      setMfaError(e.message);
    } finally {
      setOtpVerifying(false);
    }
  }, [otp]);

  // ── Generic save wrapper that handles MFA_REQUIRED ─────
  const handleSave = useCallback(async ({ url, method, body, setMsg, setSaving, successMsg }) => {
    setMsg(null);
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}${url}`, {
        method, headers: authHeaders(), body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Server signals MFA expiry mid-change
        const detail = data?.detail;
        const code = (typeof detail === "object" && detail?.code) || data?.code;
        if (res.status === 403 && code === "MFA_REQUIRED") {
          setMfaState("expired");
          setMfaError("MFA session expired. Please verify again.");
          setMfaExpiresAt(0);
          setMsg({ type: "error", text: "MFA session expired — please verify again." });
          return null;
        }
        const msg = typeof detail === "string" ? detail : (detail?.message || `HTTP ${res.status}`);
        throw new Error(msg);
      }
      // One-shot MFA: server cleared the token — reset client state.
      setMfaState("unverified");
      setMfaExpiresAt(0);
      setMsg({ type: "success", text: successMsg });
      return data;
    } catch (e) {
      setMsg({ type: "error", text: e.message });
      return null;
    } finally {
      setSaving(false);
    }
  }, []);

  // ── Save handlers ──────────────────────────────────────
  const saveEmail = useCallback(async (e) => {
    e?.preventDefault?.();
    if (!emailDraft.trim() || emailDraft === user.email) return;
    const result = await handleSave({
      url: "/api/superadmin/account/email",
      method: "PATCH",
      body: { new_email: emailDraft.trim().toLowerCase() },
      setMsg: setEmailMsg,
      setSaving: setEmailSaving,
      successMsg: "Email updated. Future sessions will use the new email.",
    });
    if (result && auth?.setUser) {
      auth.setUser({ ...user, email: result.email });
    }
  }, [emailDraft, user, handleSave, auth]);

  const savePassword = useCallback(async (e) => {
    e?.preventDefault?.();
    if (pwNew.length < 8) {
      setPwMsg({ type: "error", text: "Password must be at least 8 characters." });
      return;
    }
    if (pwNew !== pwConfirm) {
      setPwMsg({ type: "error", text: "New passwords don't match." });
      return;
    }
    const result = await handleSave({
      url: "/api/superadmin/account/password",
      method: "PATCH",
      body: { current_password: pwCurrent, new_password: pwNew },
      setMsg: setPwMsg,
      setSaving: setPwSaving,
      successMsg: "Password updated. Other sessions have been signed out.",
    });
    if (result) {
      setPwCurrent(""); setPwNew(""); setPwConfirm("");
    }
  }, [pwCurrent, pwNew, pwConfirm, handleSave]);

  const saveName = useCallback(async (e) => {
    e?.preventDefault?.();
    if (!nameDraft.trim() || nameDraft === user.full_name) return;
    const result = await handleSave({
      url: "/api/superadmin/account/profile",
      method: "PATCH",
      body: { full_name: nameDraft.trim() },
      setMsg: setNameMsg,
      setSaving: setNameSaving,
      successMsg: "Profile updated.",
    });
    if (result && auth?.setUser) {
      auth.setUser({ ...user, full_name: result.full_name });
    }
  }, [nameDraft, user, handleSave, auth]);

  // ── Convenience flags ──────────────────────────────────
  const verified = mfaState === "verified";
  const disabled = !verified;

  // ── Status pill renderer ───────────────────────────────
  const StatusPill = ({ msg }) => msg && (
    <p className={`text-xs mt-2 ${msg.type === "error" ? "text-red-400" : "text-[#00FF99]"}`}>
      {msg.text}
    </p>
  );

  return (
    <div className="space-y-5 max-w-2xl">
      <MfaBanner
        state={mfaState}
        email={user.email}
        onRequestOtp={requestOtp}
        otp={otp}
        setOtp={setOtp}
        onVerify={verifyOtp}
        otpRequesting={otpRequesting}
        otpVerifying={otpVerifying}
        otpExpiresIn={otpTimeLeft}
        mfaTimeLeft={mfaTimeLeft}
        errorMsg={mfaError}
      />

      {/* CHANGE EMAIL */}
      <form onSubmit={saveEmail} className={`${cardCls} p-5`}>
        <div className="flex items-center gap-2 mb-3">
          <Mail size={16} className="text-[#00FF99]" />
          <h3 className="text-sm font-semibold text-white">Email Address</h3>
        </div>
        <p className="text-xs text-white/40 mb-3">
          Used to sign in and receive system notifications.
        </p>
        <input
          type="email"
          className={inputCls}
          value={emailDraft}
          onChange={(e) => setEmailDraft(e.target.value)}
          disabled={disabled || emailSaving}
          placeholder="you@example.com"
        />
        <StatusPill msg={emailMsg} />
        <div className="flex justify-end mt-4">
          <button
            type="submit"
            className={primaryBtn}
            disabled={disabled || emailSaving || !emailDraft.trim() || emailDraft === user.email}
          >
            {emailSaving ? "Saving…" : "Update email"}
          </button>
        </div>
      </form>

      {/* CHANGE PASSWORD */}
      <form onSubmit={savePassword} className={`${cardCls} p-5`}>
        <div className="flex items-center gap-2 mb-3">
          <KeyRound size={16} className="text-[#00FF99]" />
          <h3 className="text-sm font-semibold text-white">Password</h3>
        </div>
        <p className="text-xs text-white/40 mb-3">
          Updating your password signs out all other active sessions.
        </p>

        <label className="text-xs text-white/60 block mb-1">Current password</label>
        <div className="relative mb-3">
          <input
            type={pwShowCurrent ? "text" : "password"}
            className={`${inputCls} pr-10`}
            value={pwCurrent}
            onChange={(e) => setPwCurrent(e.target.value)}
            disabled={disabled || pwSaving}
          />
          <button type="button" onClick={() => setPwShowCurrent((v) => !v)}
                  className="absolute inset-y-0 right-3 flex items-center text-white/40 hover:text-white">
            {pwShowCurrent ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        </div>

        <label className="text-xs text-white/60 block mb-1">New password (min 8)</label>
        <div className="relative mb-3">
          <input
            type={pwShowNew ? "text" : "password"}
            className={`${inputCls} pr-10`}
            value={pwNew}
            onChange={(e) => setPwNew(e.target.value)}
            disabled={disabled || pwSaving}
            minLength={8}
          />
          <button type="button" onClick={() => setPwShowNew((v) => !v)}
                  className="absolute inset-y-0 right-3 flex items-center text-white/40 hover:text-white">
            {pwShowNew ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        </div>

        <label className="text-xs text-white/60 block mb-1">Confirm new password</label>
        <input
          type={pwShowNew ? "text" : "password"}
          className={inputCls}
          value={pwConfirm}
          onChange={(e) => setPwConfirm(e.target.value)}
          disabled={disabled || pwSaving}
          minLength={8}
        />

        <StatusPill msg={pwMsg} />
        <div className="flex justify-end mt-4">
          <button
            type="submit"
            className={primaryBtn}
            disabled={disabled || pwSaving || !pwCurrent || pwNew.length < 8 || pwNew !== pwConfirm}
          >
            {pwSaving ? "Saving…" : "Update password"}
          </button>
        </div>
      </form>

      {/* CHANGE NAME */}
      <form onSubmit={saveName} className={`${cardCls} p-5`}>
        <div className="flex items-center gap-2 mb-3">
          <UserIcon size={16} className="text-[#00FF99]" />
          <h3 className="text-sm font-semibold text-white">Display Name</h3>
        </div>
        <p className="text-xs text-white/40 mb-3">
          Shown next to your activity in audit logs and admin tooling.
        </p>
        <input
          type="text"
          className={inputCls}
          value={nameDraft}
          onChange={(e) => setNameDraft(e.target.value)}
          disabled={disabled || nameSaving}
          maxLength={200}
        />
        <StatusPill msg={nameMsg} />
        <div className="flex justify-end mt-4">
          <button
            type="submit"
            className={primaryBtn}
            disabled={disabled || nameSaving || !nameDraft.trim() || nameDraft === user.full_name}
          >
            {nameSaving ? "Saving…" : "Update name"}
          </button>
        </div>
      </form>
    </div>
  );
}
